/**
 * Helpers for game LP `localization/settings.json`.
 * @module App/Services/Localizations/localeSettings
 */

/** Top-level `settings.json` keys that are SEO meta, not locales. */
const LOCALE_SETTINGS_META_KEYS = new Set([
    'XDefaultHref',
    'canonicalOrigin',
    'socialImageUrl',
]);

/**
 * @param {string} key
 * @param {unknown} value
 * @returns {boolean}
 */
const isLocaleEntry = (key, value) =>
    !LOCALE_SETTINGS_META_KEYS.has(key) &&
    Boolean(value) &&
    typeof value === 'object' &&
    !Array.isArray(value);

/**
 * Locale key with `role === "main"` from `localization/settings.json`.
 * The main locale must also be `"active": true`.
 *
 * @param {Object} localeSettings
 * @returns {string}
 */
export const getMainLocale = (localeSettings) => {
    const main = Object.keys(localeSettings).find(
        (key) =>
            isLocaleEntry(key, localeSettings[key]) &&
            localeSettings[key].role === 'main' &&
            localeSettings[key].active
    );

    if (!main) {
        throw new Error(
            'localization/settings.json must include one locale with role "main" and "active": true'
        );
    }

    return main;
};

/**
 * Locale keys with `"active": true` from `localization/settings.json`.
 *
 * @param {Object} localeSettings
 * @returns {string[]}
 */
export const getActiveLanguages = (localeSettings) =>
    Object.keys(localeSettings).filter(
        (key) =>
            isLocaleEntry(key, localeSettings[key]) &&
            localeSettings[key].active
    );

/**
 * Map of active locale keys to runtime language attributes for `LanguageProvider`.
 *
 * @param {Object} localeSettings
 * @param {{ includeManifestUrls?: boolean }} [options]
 * @returns {Record<string, { langCode: string, manifestURL?: string }>}
 */
export const getLocaleMap = (
    localeSettings,
    { includeManifestUrls = false } = {}
) =>
    getActiveLanguages(localeSettings).reduce((map, key) => {
        const { code, role } = localeSettings[key];

        if (!code) {
            throw new Error(
                `localization/settings.json locale "${key}" must include "code"`
            );
        }

        map[key] = { langCode: code };

        if (includeManifestUrls) {
            map[key].manifestURL =
                role === 'main'
                    ? '/site.webmanifest'
                    : `/${key}/site.webmanifest`;
        }

        return map;
    }, {});

/**
 * Keeps only entries whose keys are in `activeLanguages`.
 *
 * @param {Object<string, *>|null|undefined} byLocale
 * @param {string[]} activeLanguages
 * @returns {Object<string, *>|undefined}
 */
export const pickActiveLocaleEntries = (byLocale, activeLanguages) => {
    if (!byLocale || typeof byLocale !== 'object' || Array.isArray(byLocale)) {
        return byLocale;
    }

    return Object.fromEntries(
        activeLanguages
            .filter((key) => byLocale[key] != null)
            .map((key) => [key, byLocale[key]])
    );
};

/**
 * Runtime locale fields derived from `localization/settings.json`.
 * Spread onto the LP `config` object once; callers read `config.defaultLanguage`,
 * `config.activeLanguages`, and `config.localeMap` instead of calling these helpers again.
 * When `pushCampaignConfig` is passed, inactive locale keys are stripped so they
 * do not remain on runtime `config`.
 *
 * @param {Object} localeSettings
 * @param {{ includeManifestUrls?: boolean, pushCampaignConfig?: Object }} [options]
 * @returns {{ defaultLanguage: string, activeLanguages: string[], localeMap: Record<string, { langCode: string, manifestURL?: string }>, pushCampaignConfig?: Object }}
 */
export const getLocaleRuntimeFields = (localeSettings, options = {}) => {
    const { includeManifestUrls = false, pushCampaignConfig } = options;
    const activeLanguages = getActiveLanguages(localeSettings);
    const fields = {
        activeLanguages,
        defaultLanguage: getMainLocale(localeSettings),
        localeMap: getLocaleMap(localeSettings, { includeManifestUrls }),
    };

    if (pushCampaignConfig !== undefined) {
        fields.pushCampaignConfig = pickActiveLocaleEntries(
            pushCampaignConfig,
            activeLanguages
        );
    }

    return fields;
};
